import { LogOut, Lock } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Avatar, AvatarFallback, AvatarImage } from '~shared/shadcn/ui/avatar';
import { Button } from '~shared/shadcn/ui/button';
import { cn } from '~shared/shadcn/lib/utils';

interface UserMenuProps {
  user?: {
    name: string;
    email: string;
    avatarUrl?: string;
  };
  onChangePassword?: () => void;
  onLogout?: () => void;
}

type MenuCoords = {
  top: number;
  right: number;
};

/**
 * Portal + fixed coords from the avatar's getBoundingClientRect.
 * Avoids Radix/Floating-UI transform bugs and sticky/backdrop-filter
 * clipping that mis-placed the menu on narrow viewports.
 */
export const UserMenu = ({
  user = {
    name: 'Zoe Winters',
    email: 'zoe@studiomanager.com',
    avatarUrl: '',
  },
  onLogout,
  onChangePassword,
}: UserMenuProps) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<MenuCoords | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const updateCoords = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    // clientWidth excludes the scrollbar; matches fixed positioning containing block
    const viewportWidth = document.documentElement.clientWidth;
    setCoords({
      top: rect.bottom + 8,
      right: Math.max(8, viewportWidth - rect.right),
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateCoords();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    const onReposition = () => {
      updateCoords();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  const menu =
    open && coords
      ? createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role='menu'
            aria-label='Account menu'
            style={{ top: coords.top, right: coords.right }}
            className={cn(
              'fixed z-[200] w-64',
              'rounded-md border border-border bg-popover p-2 text-popover-foreground shadow-md',
              'border-t-2 border-t-[#FFD60A]/30',
            )}
          >
            <div className='flex flex-col gap-0.5 px-2 py-1.5'>
              <span className='text-sm font-medium'>{user.name}</span>
              <span className='truncate text-xs text-muted-foreground'>
                {user.email}
              </span>
            </div>

            <div className='my-1 h-px bg-border' />

            <button
              type='button'
              role='menuitem'
              className='flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-muted/60'
              onClick={() => {
                setOpen(false);
                onChangePassword?.();
              }}
            >
              <Lock className='size-4 text-muted-foreground' />
              Change Password
            </button>

            <div className='my-1 h-px bg-border' />

            <button
              type='button'
              role='menuitem'
              className='flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50'
              onClick={() => {
                setOpen(false);
                onLogout?.();
              }}
            >
              <LogOut className='size-4' />
              Log out
            </button>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className='relative shrink-0'>
      <Button
        ref={triggerRef}
        type='button'
        variant='ghost'
        size='icon'
        className='relative size-9 shrink-0 hover:bg-muted/60'
        aria-haspopup='menu'
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        <Avatar className='size-8'>
          {user.avatarUrl ? (
            <AvatarImage src={user.avatarUrl} alt={user.name} />
          ) : (
            <AvatarFallback className='bg-[#FFF8E1] font-semibold text-[#C18C00]'>
              {user.name.charAt(0).toUpperCase()}
            </AvatarFallback>
          )}
        </Avatar>
      </Button>
      {menu}
    </div>
  );
};
